import '../css/main.css';
import { initForms } from './forms.js';
import { track, initTracking } from './track.js';

const root = document.documentElement;
root.classList.remove('no-js');
root.classList.add('js');

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- En-tête : fond opaque après le haut de page ---------- */
function initMasthead() {
  const head = document.querySelector('[data-masthead]');
  if (!head) return;
  let ticking = false;
  const update = () => {
    head.classList.toggle('is-solid', window.scrollY > 24 || document.body.classList.contains('menu-open'));
    ticking = false;
  };
  window.addEventListener('scroll', () => {
    if (!ticking) { ticking = true; requestAnimationFrame(update); }
  }, { passive: true });
  update();
}

/* ---------- Menu mobile accessible ---------- */
function initMenu() {
  const btn = document.querySelector('[data-menu-toggle]');
  const drawer = document.querySelector('[data-drawer]');
  if (!btn || !drawer) return;
  const close = (focusBtn = true) => {
    btn.setAttribute('aria-expanded', 'false');
    drawer.hidden = true;
    document.body.classList.remove('menu-open');
    if (focusBtn) btn.focus();
  };
  btn.addEventListener('click', () => {
    const open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', String(open));
    drawer.hidden = !open;
    document.body.classList.toggle('menu-open', open);
    document.querySelector('[data-masthead]')?.classList.add('is-solid');
    if (open) drawer.querySelector('a')?.focus();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && btn.getAttribute('aria-expanded') === 'true') close();
  });
  drawer.addEventListener('click', (e) => { if (e.target.closest('a')) close(false); });
  window.matchMedia('(min-width: 1081px)').addEventListener('change', (m) => { if (m.matches) close(false); });
}

/* ---------- Récit cinématique ---------- */
function initCinema() {
  const cinema = document.querySelector('[data-cinema]');
  if (!cinema) return;
  const chapters = [...cinema.querySelectorAll('[data-chapter]')];
  const stills = [...cinema.querySelectorAll('[data-still]')];
  const dots = [...cinema.querySelectorAll('[data-progress]')];
  let active = 0;

  const setChapter = (n) => {
    if (n === active) return;
    active = n;
    cinema.classList.toggle('is-telling', n >= 1 && n <= 4);
    cinema.dataset.side = n === 2 || n === 4 ? 'right' : 'left';
    dots.forEach((d) => d.classList.toggle('is-active', Number(d.dataset.progress) === n));
    // Mode images fixes : une image par chapitre, en fondu (ignoré quand l'effet lumière tourne).
    if (root.classList.contains('is-webgl')) return;
    const still = [0, 1, 2, 3, 4, 0][n] ?? 0;
    stills.forEach((s) => {
      const on = Number(s.dataset.still) === still;
      const tpl = s.querySelector('template');
      if (on && tpl) s.replaceChildren(tpl.content.cloneNode(true));
      s.classList.toggle('is-active', on);
    });
  };

  // Si l'effet lumière s'arrête (contexte graphique perdu), les images fixes reprennent au chapitre en cours.
  window.addEventListener('jg:webgl-off', () => { const n = active; active = -1; setChapter(n); });
  if (!reduceMotion.matches) cinema.classList.add('is-motion');
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (en.isIntersecting) {
        en.target.classList.add('is-in');
        setChapter(Number(en.target.dataset.chapter));
      }
    });
  }, { rootMargin: '-45% 0px -45% 0px' });
  chapters.forEach((c) => io.observe(c));

  // L'allumage du titre attend la police, pour une seule animation propre.
  const light = () => requestAnimationFrame(() => root.classList.add('is-lit'));
  if (document.fonts && document.fonts.ready) {
    Promise.race([document.fonts.ready, new Promise((r) => setTimeout(r, 900))]).then(light);
  } else light();

  if (reduceMotion.matches) return;

  // Moteur de lumière (quelques Ko) : démarré dès que la page est affichée et le navigateur disponible.
  // L'image d'ouverture est déjà visible ; la lumière s'allume par-dessus en fondu.
  const conn = navigator.connection;
  if (conn && conn.saveData) return;
  const boot = () => {
    let scenes = [];
    try { scenes = JSON.parse(cinema.dataset.scenes || '[]'); } catch { return; }
    if (!scenes.length) return;
    import('./cinema/light.js')
      .then(({ startLight }) => startLight(cinema, scenes))
      .catch((err) => { console.warn('Effet lumière indisponible, images fixes conservées.', String(err && err.message || err)); });
  };
  const whenIdle = () => ('requestIdleCallback' in window ? requestIdleCallback(boot, { timeout: 1500 }) : setTimeout(boot, 200));
  if (document.readyState === 'complete') whenIdle();
  else window.addEventListener('load', whenIdle, { once: true });
}

/* ---------- Consentement : uniquement si un outil tiers est configuré ---------- */
function initConsent() {
  const id = document.body.dataset.ga;
  if (!id) return;
  import('./consent.js').then(({ initConsentManager }) => initConsentManager(id));
}

/* ---------- Révélation des photos par masque ---------- */
function initMasks() {
  const els = document.querySelectorAll('[data-mask]');
  if (!els.length) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-revealed'); io.unobserve(en.target); } });
  }, { rootMargin: '0px 0px -12% 0px' });
  els.forEach((el) => io.observe(el));
}

initMasthead();
initMenu();
initMasks();
initTracking();
initForms(track);
initCinema();
initConsent();
